import React from 'react';
import { MapPin, CheckCircle, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { GoogleMap } from '../components/GoogleMap';
import { AREAS_SERVED, CONTACT_INFO, WHATSAPP_MESSAGES } from '../data/mockData';

const Areas = () => {
  const whatsappLink = `https://wa.me/${CONTACT_INFO.whatsapp}?text=${encodeURIComponent(WHATSAPP_MESSAGES.generic)}`;

  const areaGroups = [
    {
      name: 'Stellenbosch & Helderberg',
      areas: AREAS_SERVED.stellenbosch,
      color: 'bg-[#039fff]'
    },
    {
      name: 'Northern Suburbs',
      areas: AREAS_SERVED.northern,
      color: 'bg-[#fb6402]'
    },
    {
      name: 'Southern Suburbs',
      areas: AREAS_SERVED.southern,
      color: 'bg-[#001A47]'
    },
    {
      name: 'Cape Town City Bowl',
      areas: AREAS_SERVED.city,
      color: 'bg-[#039fff]'
    },
    {
      name: 'Winelands',
      areas: AREAS_SERVED.winelands,
      color: 'bg-[#fb6402]'
    }
  ];

  const seoContent = [
    {
      title: 'Mini Moves Stellenbosch',
      description: 'Professional mini-move and small removal services throughout Stellenbosch, including student accommodation moves, apartment relocations, and furniture delivery. Same-day and scheduled options available.'
    },
    {
      title: 'Delivery Services Cape Town',
      description: 'Fast and reliable local delivery across Greater Cape Town. From CBD to Northern and Southern Suburbs, we handle everything from documents to small furniture with real-time tracking.'
    },
    {
      title: 'Small Removals Western Cape',
      description: 'Affordable small removal services across the Western Cape. Whether you\'re moving from Stellenbosch to Cape Town or within the same area, we provide care-first handling with straps and blankets.'
    },
    {
      title: 'Local Courier Stellenbosch',
      description: 'Hyperlocal courier service for Stellenbosch businesses and residents. Same-day delivery, store pickups, marketplace collections, and urgent on-demand options with WhatsApp updates.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#001A47] via-[#002B6B] to-[#001A47] text-white py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <MapPin className="h-16 w-16 text-[#039fff] mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Areas We Serve</h1>
            <p className="text-xl text-gray-300 mb-8">
              Covering Stellenbosch, Cape Town, and the Greater Western Cape
            </p>
            <a href={whatsappLink} target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-[#fb6402] hover:bg-[#e55a02] text-white px-8">
                <MessageCircle className="mr-2 h-5 w-5" />
                Check Your Area
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Interactive Map */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-bold text-[#001A47] mb-6 text-center">Service Area Map</h2>
            <Card>
              <CardContent className="p-4">
                <GoogleMap height="450px" showMarkers={true} />
                <div className="flex items-center justify-center gap-6 mt-4 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full bg-[#fb6402]"></div>
                    <span className="text-gray-600">Primary Service Areas</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full bg-[#039fff]"></div>
                    <span className="text-gray-600">Extended Service Areas</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Service Areas Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#001A47] mb-4">Our Service Areas</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We provide fast and reliable delivery and mini-move services across these locations
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {areaGroups.map((group, index) => (
              <Card key={index} className="hover:shadow-xl transition-shadow">
                <CardContent className="p-6">
                  <div className={`${group.color} h-2 w-16 rounded-full mb-4`}></div>
                  <h3 className="text-xl font-bold text-[#001A47] mb-4">{group.name}</h3>
                  <ul className="space-y-2">
                    {group.areas.map((area, idx) => (
                      <li key={idx} className="flex items-start text-sm text-gray-700">
                        <CheckCircle className="h-4 w-4 text-[#039fff] mr-2 flex-shrink-0 mt-0.5" />
                        {area}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Outside Service Area */}
      <section className="py-16 bg-[#039fff]/5">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-[#001A47] mb-4">Outside These Areas?</h2>
            <p className="text-lg text-gray-600 mb-8">
              We're always expanding! Contact us to check if we can service your location.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={whatsappLink} target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="bg-[#fb6402] hover:bg-[#e55a02]">
                  <MessageCircle className="mr-2 h-5 w-5" />
                  WhatsApp Us Your Area
                </Button>
              </a>
              <a href="/contact">
                <Button size="lg" variant="outline">
                  Contact Support
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* SEO Content Blocks */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-[#001A47] text-center mb-12">Local Services</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {seoContent.map((content, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-[#001A47] mb-3">{content.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{content.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Coverage Stats */}
      <section className="py-16 bg-gradient-to-r from-[#001A47] to-[#002B6B] text-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto text-center">
            <div>
              <p className="text-4xl font-bold text-[#039fff] mb-2">50+</p>
              <p className="text-gray-300">Areas Covered</p>
            </div>
            <div>
              <p className="text-4xl font-bold text-[#fb6402] mb-2">100km+</p>
              <p className="text-gray-300">Service Radius</p>
            </div>
            <div>
              <p className="text-4xl font-bold text-[#039fff] mb-2">Same Day</p>
              <p className="text-gray-300">Available Options</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Areas;
